import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Center,
  Heading,
  HStack,
  Pressable,
  Spacer,
  Spinner,
  Text,
  VStack,
} from "native-base";
import { Helmet } from "react-helmet-async";
import { RiFolderWarningLine } from "react-icons/ri";
import DatePicker from "react-datepicker";
import { useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment";
import qs from "qs";
import colors from "../../../constants/colors";
import API from "../../../api";
import { StateContext, StatusContext } from "../../../contexts";
import Quotation from "../../../components/quotation";
import DatePickerCustomInput from "../../../components/datePickerCustomInput";

function AgencyQuotationsPage() {
  const state = useContext(StateContext);
  const status = useContext(StatusContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [initialDate, setInitialDate] = useState(
    searchParams.get("initialDate")
      ? moment(searchParams.get("initialDate")).toDate()
      : moment().add(5, "days").startOf("day").toDate(),
  );
  const [finalDate, setFinalDate] = useState(
    searchParams.get("finalDate")
      ? moment(searchParams.get("finalDate")).toDate()
      : moment().add(12, "days").endOf("day").milliseconds(0).toDate(),
  );
  const [quotations, setQuotations] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const getQuotations = useCallback(
    async (signal) => {
      try {
        setIsLoading(true);
        const query = qs.stringify({
          itinerary: {
            type: "origin",
            isReturn: false,
            departureDate: {
              $gte: initialDate.toISOString(),
              $lte: finalDate.toISOString(),
            },
          },
        });
        const {
          data: {
            data,
            meta: { total: _total },
          },
        } = await API(state.jwt).get(
          `/quotations/agency?${query}`,
          signal ? { signal } : {},
        );
        setTotal(_total);
        setQuotations(data);
      } catch (error) {
        status.updateErrorMessage(error);
      }
      setIsLoading(false);
    },
    [initialDate, finalDate, state.jwt, status],
  );

  const updateSearchParams = useCallback(() => {
    navigate(
      {
        search: qs.stringify({
          initialDate: initialDate.toISOString(),
          finalDate: finalDate.toISOString(),
        }),
      },
      { replace: true },
    );
  }, [navigate, initialDate, finalDate]);

  useEffect(() => {
    updateSearchParams();
    const controller = new AbortController();
    getQuotations(controller.signal);
    return () => {
      controller.abort();
    };
  }, [getQuotations, updateSearchParams]);

  const onApplyFilters = () => {
    updateSearchParams();
    getQuotations();
  };

  const isFilterApplied = useMemo(
    () =>
      searchParams.get("initialDate") === initialDate.toISOString() &&
      searchParams.get("finalDate") === finalDate.toISOString(),
    [searchParams, initialDate, finalDate],
  );

  return (
    <VStack>
      <Helmet>
        <title>Cotizaciones</title>
      </Helmet>
      <HStack paddingY={8}>
        <Heading color={colors.secondary} fontSize="2xl">
          Cotizaciones
        </Heading>
      </HStack>
      <Text>Fecha de inicio de viaje</Text>
      <Spacer height={2} />
      <HStack space={5} alignItems="flex-end" zIndex={1}>
        <DatePicker
          selected={initialDate}
          onChange={setInitialDate}
          customInput={<DatePickerCustomInput label="Rango inicial" />}
          minDate={moment().add(5, "days").startOf("day").toDate()}
          maxDate={finalDate}
          dateFormat="yyyy-MM-dd"
        />
        <DatePicker
          selected={finalDate}
          onChange={setFinalDate}
          customInput={<DatePickerCustomInput label="Rango inicial" />}
          minDate={initialDate}
          dateFormat="yyyy-MM-dd"
        />
        <Pressable
          backgroundColor={colors.primary}
          paddingX={4}
          paddingY={1}
          borderRadius={5}
          onPress={onApplyFilters}
          disabled={isFilterApplied}>
          <Text color={colors.white}>Aplicar</Text>
        </Pressable>
      </HStack>
      <Spacer height={2} />
      <Text fontSize="sm" textAlign="right">
        Se han encontrado {total} resultado{total !== 1 && "s"}
      </Text>
      <Spacer height={5} />
      <HStack flexWrap="wrap" style={{ rowGap: 20, columnGap: 5 }}>
        {isLoading ? (
          <Center height={300} width="full">
            <Spinner size="lg" color={colors.primary} />
          </Center>
        ) : quotations.length ? (
          quotations.map((quotation) => (
            <Quotation key={quotation.id} quotation={quotation} />
          ))
        ) : (
          <Center height={300} width="full">
            <RiFolderWarningLine color={colors.secondary} fontSize={100} />
            <Text color={colors.secondary}>No hay información disponible</Text>
          </Center>
        )}
      </HStack>
    </VStack>
  );
}

export default AgencyQuotationsPage;
